import { AxiosError } from 'axios';
import { t } from 'i18next';
import { useEffect, useState } from 'react';

import Input from '../../../../../../components/Input';
import Modal, { ModalProps } from '../../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../../components/Notification';
import SelectImageWithCropper from '../../../../../../components/SelectImgWithCropper';
import SingleSelect from '../../../../../../components/SingleSelect';
import SelectAsyncJSX from '../../../../../../components/SelectAsync';
import HttpStatus from '../../../../../../enums/httpStatus';
import { smartbannersReq, audienceAPI } from '../../../../../../services/requests';
import { Smartbanner } from '../../../../../../types/apiResponseTypes';
import { currentDateWithTimeString } from '../../../../../../utils/helpers';
import { getOptionsFromAudiences } from '../../../../../../utils/getSelectOptions';
import * as s from './styles';

type SelectOption = {
  value: number;
  label: string;
};

type Props = ModalProps & {
  reqGetAllBanners: () => void;
  closeModal: () => void;
  bannerSelected?: Smartbanner | null;
};

const extractDateParts = (datePart: string) => {
  const [part1, part2, part3] = datePart.split('-');

  if (!part1 || !part2 || !part3) {
    return null;
  }

  if (part1.length === 4) {
    return {
      year: part1,
      month: part2,
      day: part3,
    };
  }

  return {
    year: part3,
    month: part2,
    day: part1,
  };
};

const formatDateTimeToInput = (value?: string | null): string => {
  if (!value) return '';

  const normalizedValue = value.replace('Z', '').replace(' ', 'T');
  const [datePart, rawTimePart = ''] = normalizedValue.split('T');

  const parts = extractDateParts(datePart);

  if (!parts) return '';

  const sanitizedTime = rawTimePart.replace('Z', '');
  const [hours = '00', minutes = '00'] = sanitizedTime.split(':');

  const dateISO = `${parts.year}-${parts.month.padStart(2, '0')}-${parts.day.padStart(2, '0')}`;
  const timeISO = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;

  return `${dateISO}T${timeISO}`;
};

const formatDateTimeToPayload = (input: string | number | Date) =>
  new Date(input).toISOString().slice(0, 19).replace('T', ' ');

const CreateOrEditBannerModal = ({
  reqGetAllBanners,
  closeModal,
  bannerSelected,
  ...props
}: Props) => {
  const isEdit = Boolean(bannerSelected?.id);

  const getDefaultShowOption = (): SelectOption => ({
    value: 1,
    label: t('show'),
  });

  const [image, setImage] = useState('');
  const [link, setLink] = useState('');
  const [date, setDate] = useState(() => formatDateTimeToInput(currentDateWithTimeString()));
  const [startDate, setStartDate] = useState('');
  const [show, setShow] = useState<SelectOption>(getDefaultShowOption);
  const [description, setDescription] = useState('');
  const [useAudience, setUseAudience] = useState(false);
  const [audienceOption, setAudienceOption] = useState<{
    label: string;
    value: number;
  } | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const resetForm = () => {
    setImage('');
    setLink('');
    setDate(formatDateTimeToInput(currentDateWithTimeString()));
    setStartDate('');
    setShow(getDefaultShowOption());
    setDescription('');
    setUseAudience(false);
    setAudienceOption(null);
  };

  useEffect(() => {
    if (isEdit && bannerSelected) {
      setImage(bannerSelected.image ?? '');
      setLink(bannerSelected.link ?? '');
      setDate(
        formatDateTimeToInput(bannerSelected.expire_in) ||
        formatDateTimeToInput(currentDateWithTimeString()),
      );
      setStartDate(formatDateTimeToInput(bannerSelected.start_date) || '');
      setDescription(bannerSelected.description ?? '');
      setShow(
        bannerSelected.show
          ? { value: 1, label: t('show') }
          : { value: 0, label: t('notShow') },
      );

      // Set audience if exists
      if (bannerSelected.audience_id && bannerSelected.audience) {
        setUseAudience(true);
        const label = `${bannerSelected.audience.id} - ${bannerSelected.audience.description}`;
        setAudienceOption({
          label: label,
          value: bannerSelected.audience.id,
        });
      } else {
        setUseAudience(false);
        setAudienceOption(null);
      }
    } else if (!isEdit) {
      setImage('');
      setLink('');
      setDate(formatDateTimeToInput(currentDateWithTimeString()));
      setStartDate('');
      setShow({ value: 1, label: t('show') });
      setDescription('');
      setUseAudience(false);
      setAudienceOption(null);
    }
  }, [bannerSelected, isEdit]);

  const showOptions: SelectOption[] = [
    { value: 1, label: t('show') },
    { value: 0, label: t('notShow') },
  ];

  const handleSubmit = async () => {
    setIsLoading(true);

    const payload = {
      image,
      link,
      expire_in: formatDateTimeToPayload(date),
      start_date: startDate ? formatDateTimeToPayload(startDate) : null,
      show: show.value,
      description,
      audience_id: useAudience && audienceOption ? audienceOption.value : null,
    };

    try {
      const response = isEdit && bannerSelected
        ? await smartbannersReq.update(payload, bannerSelected.id)
        : await smartbannersReq.store(payload);

      const isSuccess = isEdit
        ? response.status === HttpStatus.OK
        : response.status === HttpStatus.CREATED;

      if (isSuccess) {
        frontendNotification({
          message: t(isEdit ? 'successfullyUpdated' : 'successfullyCreated'),
          type: 'success',
        });

        reqGetAllBanners();
        resetForm();
        closeModal();
      } else {
        throw response;
      }
    } catch (err) {
      if (!smartbannersReq.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }

    setIsLoading(false);
  };

  return (
    <Modal
      {...props}
      action={handleSubmit}
      isLoading={isLoading}
      closeModal={closeModal}
    // bodyStyle={{ overflowX: 'scroll' }}
    >
      <s.AddBannerContainer>
        <s.Row>
          <s.Item>
            <b>{`${t('maxImaSizeAndResolution')}: 1mb`}</b>
          </s.Item>
        </s.Row>

        <s.Row>
          <s.Item>
            <SelectImageWithCropper
              getImage={(i) => setImage(i)}
              requiredCrop
              validateDimensions={false}
            />
          </s.Item>
        </s.Row>
        <s.Row>
          <s.Item className="img-area">
            <img alt={description} src={image} style={{ maxWidth: '100%', minWidth: '100%' }} />
          </s.Item>
        </s.Row>

        <s.Row>
          <s.Item>
            <p>{t('link') as string}</p>
            <Input
              value={link}
              onChange={(v) => setLink(v.currentTarget.value)}
              disabled={isLoading}
            />
          </s.Item>
        </s.Row>

        <s.Row>
          <s.Item>
            <p>{t('startDate') as string}</p>
            <input
              className="input"
              type="datetime-local"
              value={startDate}
              onChange={(v) => setStartDate(v.currentTarget.value)}
              disabled={isLoading}
              step={60}
            />
          </s.Item>
          <s.Item>
            <p>{t('expirationDate') as string}</p>
            <input
              className="input"
              type="datetime-local"
              value={date}
              onChange={(v) => setDate(v.currentTarget.value)}
              disabled={isLoading}
              step={60}
            />
          </s.Item>
        </s.Row>

        <s.Row>
          <s.Item>
            <p>{t('show') as string}</p>
            <SingleSelect
              placeholder={t('show') as string}
              options={showOptions}
              onChange={(v: any) => setShow(v)}
              value={show}
              isDisabled={isLoading}
            />
          </s.Item>
        </s.Row>

        <s.Row>
          <s.Item>
            <p>{t('description') as string}</p>
            <Input
              value={description}
              onChange={(v) => setDescription(v.currentTarget.value)}
              disabled={isLoading}
            />
          </s.Item>
        </s.Row>

        <s.Row>
          <s.Item>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
              <input
                type="checkbox"
                id="useAudience"
                checked={useAudience}
                onChange={(e) => {
                  setUseAudience(e.target.checked);
                  if (!e.target.checked) {
                    setAudienceOption(null);
                  }
                }}
                disabled={isLoading}
                style={{ marginRight: '0.5rem' }}
              />
              <label htmlFor="useAudience" style={{ cursor: 'pointer' }}>
                {t('useAudience') as string}
              </label>
            </div>
            {useAudience && (
              <div>
                <p>{t('audience') as string}</p>
                <SelectAsyncJSX
                  value={audienceOption}
                  onChange={setAudienceOption}
                  request={audienceAPI}
                  reqResponseToOption={getOptionsFromAudiences}
                  placeholder={t('selectAudience')}
                  disabled={isLoading}
                />
              </div>
            )}
          </s.Item>
        </s.Row>
      </s.AddBannerContainer>
    </Modal>
  );
};

export default CreateOrEditBannerModal;
